import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Github, Link2, Unlink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { SettingsLayout } from './SettingsLayout';
import { getOAuthAccounts, unlinkOAuth } from '@/api/settings';
import { useState } from 'react';

const backendBase = import.meta.env.VITE_BACKEND_URL || '';
const oauthBase = backendBase ? backendBase.replace(/\/$/, '') : '';
const githubConnectHref = `${oauthBase}/oauth/github`;
const googleConnectHref = `${oauthBase}/oauth/google`;

export function LinkedAccountsPage() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['oauth-accounts'],
    queryFn: getOAuthAccounts,
  });

  const unlinkMutation = useMutation({
    mutationFn: unlinkOAuth,
    onSuccess: (result, provider) => {
      if (result.success) {
        setSuccess(`Successfully unlinked your ${provider} account.`);
        setError(null);
        queryClient.invalidateQueries({ queryKey: ['oauth-accounts'] });
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error || 'Failed to unlink account');
      }
    },
    onError: () => {
      setError('Failed to unlink account');
    },
  });

  const handleUnlink = (provider: string) => {
    setError(null);
    setSuccess(null);
    unlinkMutation.mutate(provider);
  };

  if (isLoading) {
    return (
      <SettingsLayout>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </SettingsLayout>
    );
  }

  const linkedProviders = data?.linked_providers || {};
  const hasGithub = 'github' in linkedProviders;
  const hasGoogle = 'google' in linkedProviders;

  return (
    <SettingsLayout>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Linked Accounts
          </CardTitle>
          <CardDescription>
            Manage your connected OAuth accounts.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {success && (
            <Alert className="bg-green-50 border-green-200">
              <AlertDescription className="text-green-800">
                {success}
              </AlertDescription>
            </Alert>
          )}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* GitHub */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-gray-900 flex items-center justify-center">
                <Github className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="font-medium">GitHub</p>
                {hasGithub ? (
                  <p className="text-sm text-muted-foreground">
                    Connected as {linkedProviders.github.provider_user_login || linkedProviders.github.provider_user_id}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">Not connected</p>
                )}
              </div>
            </div>
            {hasGithub ? (
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="bg-green-100 text-green-700">
                  Connected
                </Badge>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleUnlink('github')}
                  disabled={unlinkMutation.isPending}
                >
                  {unlinkMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Unlink className="h-4 w-4 mr-1" />
                      Unlink
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <Button variant="outline" size="sm" asChild>
                <a href={githubConnectHref}>
                  <Link2 className="h-4 w-4 mr-1" />
                  Connect
                </a>
              </Button>
            )}
          </div>

          {/* Google */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-white border flex items-center justify-center">
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
              </div>
              <div>
                <p className="font-medium">Google</p>
                {hasGoogle ? (
                  <p className="text-sm text-muted-foreground">
                    Connected
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">Not connected</p>
                )}
              </div>
            </div>
            {hasGoogle ? (
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="bg-green-100 text-green-700">
                  Connected
                </Badge>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleUnlink('google')}
                  disabled={unlinkMutation.isPending}
                >
                  {unlinkMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Unlink className="h-4 w-4 mr-1" />
                      Unlink
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <Button variant="outline" size="sm" asChild>
                <a href={googleConnectHref}>
                  <Link2 className="h-4 w-4 mr-1" />
                  Connect
                </a>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </SettingsLayout>
  );
}
